import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Mail, Lock, User, Phone, MapPin, ArrowLeft, ShoppingBag, Briefcase, Gift } from "lucide-react";
import { Logo, Alert } from "../components/ui";
import { INDONESIAN_CITIES } from "../data/cities";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [role, setRole] = useState(params.get("role") === "WORKER" ? "WORKER" : "CUSTOMER");
  const [form, setForm] = useState({
    display_name: "",
    full_legal_name: "",
    email: "",
    phone: "",
    city: "",
    password: "",
    referral_code: params.get("ref") ? params.get("ref").toUpperCase().slice(0, 16) : "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 12) {
      setError("Kata sandi minimal 12 karakter untuk keamanan.");
      return;
    }
    setLoading(true);
    try {
      const user = await register({ ...form, role });
      navigate(user.role === "ADMIN" ? "/admin" : user.role === "WORKER" ? "/worker" : "/customer");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f6f8fc] py-8 px-5">
      <div className="w-full max-w-lg mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-brand-500 font-semibold mb-6" data-testid="back-home">
          <ArrowLeft className="h-4 w-4" /> Kembali
        </Link>
        <div className="mb-6"><Logo /></div>
        <div className="card p-6 md:p-8">
          <h2 className="text-brand-800">Buat akun gratis</h2>
          <p className="text-brand-400 mt-1">Sudah punya akun?{" "}
            <Link to="/login" className="text-brand-600 font-semibold" data-testid="link-login">Masuk</Link>
          </p>

          {/* Role toggle */}
          <div className="mt-6 grid grid-cols-2 gap-3">
            <RoleButton active={role === "CUSTOMER"} onClick={() => setRole("CUSTOMER")} icon={ShoppingBag}
              title="Saya Pelanggan" desc="Cari & pesan jasa" testid="role-customer" />
            <RoleButton active={role === "WORKER"} onClick={() => setRole("WORKER")} icon={Briefcase}
              title="Saya Pekerja" desc="Tawarkan jasa" testid="role-worker" />
          </div>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="register-form">
            {error && <Alert testid="register-error">{error}</Alert>}
            <Field label="Nama tampilan" icon={User} value={form.display_name} onChange={set("display_name")} placeholder="Contoh: Budi" testid="reg-display-name" required />
            <Field label="Nama lengkap sesuai KTP" icon={User} value={form.full_legal_name} onChange={set("full_legal_name")} placeholder="Nama legal (disimpan aman)" testid="reg-legal-name" />
            <Field label="Email" icon={Mail} type="email" value={form.email} onChange={set("email")} placeholder="nama@email.com" testid="reg-email" required />
            <Field label="Nomor HP" icon={Phone} value={form.phone} onChange={set("phone")} placeholder="08xxxxxxxxxx" testid="reg-phone" />
            <div>
              <label className="field-label">{role === "WORKER" ? "Kota domisili" : "Kota"}</label>
              <div className="relative">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-brand-300" />
                <input
                  type="text"
                  value={form.city}
                  onChange={set("city")}
                  placeholder="Contoh: Jakarta"
                  list="reg-city-list"
                  autoComplete="off"
                  className="field-input pl-12"
                  data-testid="reg-city"
                />
              </div>
              <datalist id="reg-city-list">
                {INDONESIAN_CITIES.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </div>
            <Field label="Kata sandi (min. 12 karakter)" icon={Lock} type="password" value={form.password} onChange={set("password")} placeholder="Buat kata sandi kuat" testid="reg-password" required />
            <Field label="Kode referral (opsional)" icon={Gift} value={form.referral_code} onChange={(e) => setForm({ ...form, referral_code: e.target.value.toUpperCase().slice(0, 16) })} placeholder="Masukkan kode dari temanmu" testid="reg-referral-code" />
            <button type="submit" disabled={loading} className="btn-primary w-full btn-lg" data-testid="register-submit">
              {loading ? "Memproses..." : "Daftar Sekarang"}
            </button>
            <p className="text-xs text-brand-400 text-center">
              Data pribadi Anda (email, HP, KTP) disimpan terenkripsi di brankas terpisah sesuai UU PDP.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}

function RoleButton({ active, onClick, icon: Icon, title, desc, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`rounded-2xl border-2 p-4 text-left transition ${
        active ? "border-brand-600 bg-brand-50" : "border-brand-100 bg-white hover:border-brand-200"
      }`}>
      <Icon className={`h-6 w-6 ${active ? "text-brand-600" : "text-brand-300"}`} />
      <p className="font-bold text-brand-800 mt-2">{title}</p>
      <p className="text-sm text-brand-400">{desc}</p>
    </button>
  );
}

function Field({ label, icon: Icon, type = "text", value, onChange, placeholder, testid, required }) {
  return (
    <div>
      <label className="field-label">{label}</label>
      <div className="relative">
        <Icon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-brand-300" />
        <input type={type} value={value} onChange={onChange} placeholder={placeholder}
          required={required} className="field-input pl-12" data-testid={testid} />
      </div>
    </div>
  );
}
